import { useState, useMemo, useCallback } from "react";
import { useControlPlane } from "@/data/controlPlane/context";
import { CAPABILITY_REGISTRY, getAllCategories } from "@/data/controlPlane/registry";
import UserPermissionMatrix from "@/components/UserPermissionMatrix";
import { MODE_PROFILES } from "@/data/controlPlane/profiles";
import { buildDependencyGraph } from "@/data/controlPlane/engine";
import { ROLES } from "@/data/RoleContext";
import type { UserRole } from "@/data/RoleContext";
import type {
  ResolvedCapability, BehaviorMode, ModeProfileId, Environment,
  ManagedUser, ManagedTenant, UserAccountStatus, TenantStatus,
  ChangeRequest, SimulationResult, DecisionSummary, ChangeRiskLevel,
} from "@/data/controlPlane/types";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  Shield, Eye, EyeOff, Lock, Activity,
  ChevronRight, Settings, GitBranch, FileText, AlertTriangle,
  CheckCircle, XCircle, Layers, Box, Users, Building2,
  UserCog, Search, Mail, Clock, ShieldCheck,
  ShieldX, Ban, RotateCcw, KeyRound, Info,
  Zap, ArrowRight, FlaskConical, ShieldAlert,
} from "lucide-react";

// ── Shared constants ──

const MODE_ICONS: Record<BehaviorMode, typeof Eye> = {
  active: Eye, hidden: EyeOff, read_only: Lock, shadow: Activity, internal_only: Shield,
};
const MODE_COLORS: Record<BehaviorMode, string> = {
  active: "bg-success/10 text-success border-success/20",
  hidden: "bg-muted text-muted-foreground border-border",
  read_only: "bg-warning/10 text-warning border-warning/20",
  shadow: "bg-primary/10 text-primary border-primary/20",
  internal_only: "bg-accent text-accent-foreground border-accent",
};
const TIER_COLORS: Record<string, string> = {
  core: "bg-muted text-foreground", advanced: "bg-primary/10 text-primary",
  enterprise: "bg-warning/10 text-warning", internal: "bg-destructive/10 text-destructive",
};
const CATEGORY_LABELS: Record<string, string> = {
  scheduling: "Scheduling", finance: "Finance", crm: "CRM", qa: "Quality Assurance",
  materials: "Materials", admin: "Administration", sales: "Sales", operations: "Operations",
  warehouse: "Warehouse", intelligence: "Intelligence", portal: "Portals",
  compliance: "Compliance", reporting: "Reporting", communications: "Communications",
};
const USER_STATUS_CONFIG: Record<UserAccountStatus, { icon: typeof CheckCircle; color: string; label: string }> = {
  active: { icon: CheckCircle, color: "text-success", label: "Active" },
  inactive: { icon: XCircle, color: "text-muted-foreground", label: "Inactive" },
  suspended: { icon: Ban, color: "text-destructive", label: "Suspended" },
  invited: { icon: Mail, color: "text-primary", label: "Invited" },
  deactivated: { icon: ShieldX, color: "text-muted-foreground", label: "Deactivated" },
};
const TENANT_STATUS_CONFIG: Record<TenantStatus, { color: string; label: string }> = {
  active: { color: "text-success", label: "Active" },
  trial: { color: "text-primary", label: "Trial" },
  suspended: { color: "text-destructive", label: "Suspended" },
  churned: { color: "text-muted-foreground", label: "Churned" },
  onboarding: { color: "text-warning", label: "Onboarding" },
};
const RISK_CONFIG: Record<ChangeRiskLevel, { color: string; label: string; icon: typeof Shield }> = {
  low: { color: "text-success bg-success/10 border-success/20", label: "Low Risk", icon: CheckCircle },
  medium: { color: "text-warning bg-warning/10 border-warning/20", label: "Medium Risk", icon: AlertTriangle },
  high: { color: "text-destructive bg-destructive/10 border-destructive/20", label: "High Risk", icon: ShieldAlert },
  critical: { color: "text-destructive bg-destructive/15 border-destructive/30", label: "Critical Risk", icon: ShieldX },
};

function timeAgo(iso?: string): string {
  if (!iso) return "Never";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function ControlPlanePage() {
  const cp = useControlPlane();
  const {
    resolved, environment, modeProfile, auditLog,
    setEnvironment, setModeProfile, setOverride,
    users, tenants, updateTenantStatus, updateTenantProfile,
    roles, explain, simulate,
  } = cp;

  const [selectedCap, setSelectedCap] = useState<string | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  // Simulation / Confirmation state
  const [pendingChange, setPendingChange] = useState<ChangeRequest | null>(null);
  const [simResult, setSimResult] = useState<SimulationResult | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [showExplainer, setShowExplainer] = useState<string | null>(null);

  const categories = useMemo(() => getAllCategories(), []);
  const depGraph = useMemo(() => buildDependencyGraph(resolved), [resolved]);

  const stats = useMemo(() => {
    const enabled = resolved.filter(r => r.enabled).length;
    const hidden = resolved.filter(r => r.effectiveMode === "hidden").length;
    const shadow = resolved.filter(r => r.effectiveMode === "shadow").length;
    const readOnly = resolved.filter(r => r.effectiveMode === "read_only").length;
    const overrideCount = resolved.filter(r => r.passTrace.some(t => t.pass.includes("Override"))).length;
    return { total: resolved.length, enabled, hidden, shadow, readOnly, overrideCount };
  }, [resolved]);

  const lastAuditEntry = auditLog.length > 0 ? auditLog[0] : null;

  const selectedTenant = selectedTenantId ? tenants.find(t => t.id === selectedTenantId) : null;
  const selectedResolved = selectedCap ? resolved.find(r => r.id === selectedCap) : null;
  const selectedDef = selectedCap ? CAPABILITY_REGISTRY.find(c => c.id === selectedCap) : null;
  const selectedNode = selectedCap ? depGraph.find(n => n.id === selectedCap) : null;
  const explainerData = showExplainer ? explain(showExplainer) : null;

  // ── Safe action helpers ──
  const requestChange = useCallback((change: ChangeRequest) => {
    const result = simulate(change);
    setSimResult(result);
    setPendingChange(change);
    setConfirmText("");
  }, [simulate]);

  const applyPendingChange = useCallback(() => {
    if (!pendingChange) return;
    switch (pendingChange.type) {
      case "capability_override": {
        const { enabled, behaviorMode } = pendingChange.payload as { enabled: boolean; behaviorMode: BehaviorMode };
        setOverride(pendingChange.targetId, enabled, behaviorMode, "Confirmed via simulation preview");
        break;
      }
      case "environment_change":
        setEnvironment(pendingChange.payload.environment as Environment);
        break;
      case "tenant_profile_change":
        updateTenantProfile(pendingChange.targetId, pendingChange.payload.modeProfile as ModeProfileId, "Confirmed via simulation preview");
        break;
      case "tenant_status_change":
        updateTenantStatus(pendingChange.targetId, pendingChange.payload.status as TenantStatus, "Confirmed via simulation preview");
        break;
      case "user_role_change":
      case "user_status_change":
      case "tenant_capability_override":
        break;
    }
    setPendingChange(null);
    setSimResult(null);
    setConfirmText("");
  }, [pendingChange, setOverride, setEnvironment, updateTenantProfile]);

  const needsTypedConfirm = simResult && (simResult.impactPreview.riskLevel === "high" || simResult.impactPreview.riskLevel === "critical");

  // Risk posture
  const riskPosture = useMemo(() => {
    const hasHighRisk = resolved.some(r => r.effectiveMode === "shadow");
    const hasDisabled = stats.hidden > 0;
    if (hasHighRisk) return { label: "Elevated", color: "text-warning" };
    if (hasDisabled) return { label: "Guarded", color: "text-muted-foreground" };
    return { label: "Normal", color: "text-success" };
  }, [resolved, stats]);

  return (
    <div className="space-y-0">
      {/* ═══ SYSTEM BAR — Governance Cockpit Header ═══ */}
      <div className="rounded-xl border border-border bg-card/60 backdrop-blur-sm mb-6">
        {/* Top row: title + controls */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground tracking-tight">Control Plane</h1>
              <p className="text-[11px] text-muted-foreground">Identity, access & capability governance</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={environment} onValueChange={(v) =>
              requestChange({ type: "environment_change", targetId: "environment", payload: { environment: v } })
            }>
              <SelectTrigger className="h-8 w-[120px] text-[11px] bg-background/50"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(["dev", "staging", "pilot", "production", "internal"] as Environment[]).map(e => (
                  <SelectItem key={e} value={e} className="text-xs capitalize">{e}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={modeProfile} onValueChange={(v) =>
              requestChange({ type: "tenant_profile_change", targetId: "global", payload: { modeProfile: v } })
            }>
              <SelectTrigger className="h-8 w-[140px] text-[11px] bg-background/50"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MODE_PROFILES.map(p => (
                  <SelectItem key={p.id} value={p.id} className="text-xs">{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Bottom row: status pills */}
        <div className="flex items-center gap-3 px-5 pb-3 flex-wrap">
          <StatusPill label="Capabilities" value={`${stats.enabled}/${stats.total}`} muted />
          <StatusPill label="Hidden" value={stats.hidden} warn={stats.hidden > 0} />
          <StatusPill label="Shadow" value={stats.shadow} warn={stats.shadow > 0} />
          <StatusPill label="Overrides" value={stats.overrideCount} warn={stats.overrideCount > 0} />
          <div className="h-4 w-px bg-border mx-1" />
          <StatusPill label="Users" value={users.length} />
          <StatusPill label="Tenants" value={tenants.length} />
          <div className="h-4 w-px bg-border mx-1" />
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground">Risk:</span>
            <span className={`text-[10px] font-semibold ${riskPosture.color}`}>{riskPosture.label}</span>
          </div>
          {lastAuditEntry && (
            <>
              <div className="h-4 w-px bg-border mx-1" />
              <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span className="truncate max-w-[180px]">{lastAuditEntry.targetId}</span>
                <span className="text-muted-foreground/50">·</span>
                <span>{timeAgo(lastAuditEntry.timestamp)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="h-9 bg-muted/40 p-0.5 rounded-lg">
          <TabsTrigger value="users" className="text-[11px] gap-1.5 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"><Users className="h-3.5 w-3.5" /> Users</TabsTrigger>
          <TabsTrigger value="roles" className="text-[11px] gap-1.5 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"><UserCog className="h-3.5 w-3.5" /> Roles</TabsTrigger>
          <TabsTrigger value="tenants" className="text-[11px] gap-1.5 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"><Building2 className="h-3.5 w-3.5" /> Tenants</TabsTrigger>
          <TabsTrigger value="modules" className="text-[11px] gap-1.5 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"><Box className="h-3.5 w-3.5" /> Modules</TabsTrigger>
          <TabsTrigger value="effective" className="text-[11px] gap-1.5 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"><Settings className="h-3.5 w-3.5" /> Effective</TabsTrigger>
          <TabsTrigger value="dependencies" className="text-[11px] gap-1.5 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"><GitBranch className="h-3.5 w-3.5" /> Dependencies</TabsTrigger>
          <TabsTrigger value="audit" className="text-[11px] gap-1.5 rounded-md data-[state=active]:bg-background data-[state=active]:shadow-sm"><FileText className="h-3.5 w-3.5" /> Audit</TabsTrigger>
        </TabsList>

        {/* ══ USERS TAB ══ */}
        <TabsContent value="users" className="mt-5">
          <UserPermissionMatrix />
        </TabsContent>

        {/* ══ ROLES TAB ══ */}
        <TabsContent value="roles" className="mt-5 space-y-3">
          {roles.map(role => (
            <div key={role.id} className="rounded-xl border border-border bg-card/50 p-4 transition-colors hover:bg-card/80">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-primary/8 flex items-center justify-center">
                    <KeyRound className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">{role.label}</h4>
                    <p className="text-[11px] text-muted-foreground">{role.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] font-normal">{role.userCount} user{role.userCount !== 1 ? "s" : ""}</Badge>
                  {role.isSystem && <Badge variant="outline" className="text-[10px] bg-muted/50 font-normal">System</Badge>}
                </div>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Capabilities ({role.capabilityAccess.length})</p>
                <div className="flex flex-wrap gap-1">
                  {role.capabilityAccess.map(capId => {
                    const r = resolved.find(c => c.id === capId);
                    return (
                      <Badge key={capId} variant="outline" className={`text-[9px] font-normal ${r?.enabled ? "bg-success/5 text-success/80 border-success/15" : "bg-muted/30 text-muted-foreground/40 border-border"}`}>
                        {r?.label ?? capId}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* ══ TENANTS TAB ══ */}
        <TabsContent value="tenants" className="mt-5 space-y-3">
          {tenants.map(t => {
            const tc = TENANT_STATUS_CONFIG[t.status];
            const tenantUsers = users.filter(u => u.tenantId === t.id);
            const isSelected = selectedTenantId === t.id;
            return (
              <div key={t.id} className={`rounded-xl border bg-card/50 p-4 transition-all ${isSelected ? "border-primary/30 bg-card/80" : "border-border hover:bg-card/70"}`}>
                <button onClick={() => setSelectedTenantId(isSelected ? null : t.id)} className="w-full text-left">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-primary/8 flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground">{t.name}</h4>
                        <p className="text-[11px] text-muted-foreground">{t.slug} · {t.environment}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[10px] font-normal ${tc.color}`}>{tc.label}</Badge>
                      <Badge variant="outline" className="text-[10px] font-normal">{tenantUsers.length} users</Badge>
                      <Badge variant="outline" className="text-[10px] font-normal bg-muted/30">{t.modeProfile}</Badge>
                      <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${isSelected ? "rotate-90" : ""}`} />
                    </div>
                  </div>
                </button>

                {isSelected && (
                  <div className="mt-4 pt-4 border-t border-border/50 space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <InfoCell label="Status" value={tc.label} />
                      <InfoCell label="Profile" value={t.modeProfile} />
                      <InfoCell label="Environment" value={t.environment} />
                      <InfoCell label="Created" value={new Date(t.createdAt).toLocaleDateString()} />
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Select value={t.modeProfile} onValueChange={(v) =>
                        requestChange({ type: "tenant_profile_change", targetId: t.id, payload: { modeProfile: v } })
                      }>
                        <SelectTrigger className="h-8 w-[160px] text-[11px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {MODE_PROFILES.map(p => (
                            <SelectItem key={p.id} value={p.id} className="text-xs">{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {t.status === "active" && (
                        <Button size="sm" variant="outline" className="text-[11px] h-8"
                          onClick={() => requestChange({ type: "tenant_status_change", targetId: t.id, payload: { status: "suspended" } })}>
                          Suspend
                        </Button>
                      )}
                      {t.status === "suspended" && (
                        <Button size="sm" variant="outline" className="text-[11px] h-8"
                          onClick={() => requestChange({ type: "tenant_status_change", targetId: t.id, payload: { status: "active" } })}>
                          Restore
                        </Button>
                      )}
                    </div>

                    {tenantUsers.length > 0 && (
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Users in this tenant</p>
                        <div className="space-y-1">
                          {tenantUsers.map(u => (
                            <div key={u.id} className="flex items-center gap-2.5 text-[11px] p-2 rounded-lg bg-muted/15">
                              <div className="h-6 w-6 rounded-full bg-primary/8 flex items-center justify-center text-[9px] font-semibold text-primary">
                                {u.firstName[0]}{u.lastName[0]}
                              </div>
                              <span className="text-foreground font-medium">{u.displayName}</span>
                              <span className="text-muted-foreground capitalize">{u.role.replace("_", " ")}</span>
                              <Badge variant="outline" className={`text-[9px] ml-auto font-normal ${USER_STATUS_CONFIG[u.status].color}`}>{u.status}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </TabsContent>

        {/* ══ MODULES TAB ══ */}
        <TabsContent value="modules" className="mt-5 space-y-6">
          {categories.map(cat => {
            const catCaps = resolved.filter(r => r.category === cat);
            if (catCaps.length === 0) return null;
            return (
              <div key={cat}>
                <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  {CATEGORY_LABELS[cat] ?? cat} <span className="font-normal text-muted-foreground/60">({catCaps.length})</span>
                </h3>
                <div className="space-y-1.5">
                  {catCaps.map(cap => (
                    <CapabilityRow key={cap.id} cap={cap} selected={selectedCap === cap.id}
                      onClick={() => setSelectedCap(selectedCap === cap.id ? null : cap.id)}
                      onExplain={() => setShowExplainer(cap.id)} />
                  ))}
                </div>
              </div>
            );
          })}

          {selectedResolved && selectedDef && (
            <div className="rounded-xl border border-primary/20 bg-card/60 p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-foreground">{selectedResolved.label}</h3>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{selectedDef.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" className="text-[11px] h-7 gap-1 text-muted-foreground hover:text-primary" onClick={() => setShowExplainer(selectedResolved.id)}>
                    <Info className="h-3 w-3" /> Why?
                  </Button>
                  <Badge className={TIER_COLORS[selectedResolved.tier]} variant="outline">{selectedResolved.tier}</Badge>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <InfoCell label="Mode" value={selectedResolved.effectiveMode.replace("_", " ")} />
                <InfoCell label="Enabled" value={selectedResolved.enabled ? "Yes" : "No"} />
                <InfoCell label="Accessible" value={selectedResolved.accessible ? "Yes" : "No"} />
                <InfoCell label="Audit Required" value={selectedDef.auditRequired ? "Yes" : "No"} />
              </div>
              <div>
                <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Influence</h4>
                <div className="flex gap-1.5">
                  {Object.entries(selectedResolved.influence).map(([k, v]) => (
                    <Badge key={k} variant="outline" className={`text-[9px] font-normal ${v ? "bg-primary/8 text-primary border-primary/15" : "bg-muted/30 text-muted-foreground/40 border-border"}`}>
                      {k}: {v ? "✓" : "—"}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">UI Surfaces</h4>
                <div className="flex flex-wrap gap-1">
                  {selectedDef.uiSurfaces.map(s => (
                    <Badge key={s} variant="outline" className="text-[9px] font-mono font-normal">{s}</Badge>
                  ))}
                </div>
              </div>
              {selectedNode && (selectedNode.dependencies.length > 0 || selectedNode.dependents.length > 0) && (
                <div>
                  <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Dependency Chain</h4>
                  {selectedNode.dependencies.length > 0 && (
                    <p className="text-[11px] text-muted-foreground">Depends on: {selectedNode.dependencies.map(d => <Badge key={d} variant="outline" className="text-[9px] mx-0.5 font-normal">{d}</Badge>)}</p>
                  )}
                  {selectedNode.dependents.length > 0 && (
                    <p className="text-[11px] text-muted-foreground mt-1">Required by: {selectedNode.dependents.map(d => <Badge key={d} variant="outline" className="text-[9px] mx-0.5 font-normal">{d}</Badge>)}</p>
                  )}
                </div>
              )}
              {selectedResolved.passTrace.length > 0 && (
                <div>
                  <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Resolution Trace</h4>
                  <div className="space-y-1 max-h-[200px] overflow-y-auto">
                    {selectedResolved.passTrace.map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-[11px] p-2 rounded-lg bg-muted/15">
                        <Badge variant="outline" className="text-[9px] shrink-0 font-normal">{t.pass}</Badge>
                        <span className="text-muted-foreground flex-1">{t.action}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground/40 shrink-0" />
                        <span className="font-mono text-foreground text-[10px]">{t.after.enabled ? "on" : "off"} / {t.after.mode}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Separator className="bg-border/50" />
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-[11px] gap-1 h-8"
                  onClick={() => requestChange({
                    type: "capability_override", targetId: selectedResolved.id,
                    payload: { enabled: !selectedResolved.enabled, behaviorMode: selectedResolved.effectiveMode },
                  })}>
                  <FlaskConical className="h-3 w-3" /> {selectedResolved.enabled ? "Disable" : "Enable"}
                </Button>
                <Button size="sm" variant="outline" className="text-[11px] gap-1 h-8"
                  onClick={() => requestChange({
                    type: "capability_override", targetId: selectedResolved.id,
                    payload: { enabled: selectedResolved.enabled, behaviorMode: "read_only" },
                  })}>
                  Read Only
                </Button>
                <Button size="sm" variant="outline" className="text-[11px] gap-1 h-8"
                  onClick={() => requestChange({
                    type: "capability_override", targetId: selectedResolved.id,
                    payload: { enabled: selectedResolved.enabled, behaviorMode: "shadow" },
                  })}>
                  Shadow
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ══ Effective Configuration ══ */}
        <TabsContent value="effective" className="mt-5">
          <div className="rounded-xl border border-border bg-card/50">
            <div className="p-5 pb-3">
              <h3 className="text-sm font-semibold text-foreground">Effective Configuration</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">The resolved state after all 9 governance passes</p>
            </div>
            <div className="overflow-hidden">
              <div className="grid grid-cols-[1fr_70px_90px_1fr_36px] gap-0 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-5 py-2.5 bg-muted/20 border-y border-border/50">
                <span>Capability</span><span>Status</span><span>Mode</span><span>Reason</span><span></span>
              </div>
              <div className="divide-y divide-border/30 max-h-[500px] overflow-y-auto">
                {resolved.map(r => (
                  <div key={r.id} className="grid grid-cols-[1fr_70px_90px_1fr_36px] gap-0 text-[11px] px-5 py-2.5 items-center hover:bg-muted/10 transition-colors">
                    <span className="font-medium text-foreground">{r.label}</span>
                    <span>{r.enabled ? <CheckCircle className="h-3.5 w-3.5 text-success" /> : <XCircle className="h-3.5 w-3.5 text-muted-foreground/40" />}</span>
                    <Badge variant="outline" className={`text-[9px] w-fit font-normal ${MODE_COLORS[r.effectiveMode]}`}>
                      {r.effectiveMode.replace("_", " ")}
                    </Badge>
                    <span className="text-muted-foreground truncate">{r.reason}</span>
                    <button onClick={() => setShowExplainer(r.id)} className="text-muted-foreground/40 hover:text-primary transition-colors">
                      <Info className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* ══ Dependencies ══ */}
        <TabsContent value="dependencies" className="mt-5">
          <div className="rounded-xl border border-border bg-card/50 p-5">
            <h3 className="text-sm font-semibold text-foreground mb-1">Dependency Graph</h3>
            <p className="text-[11px] text-muted-foreground mb-4">Capability relationships and dependency chains</p>
            <div className="space-y-2">
              {depGraph.filter(n => n.dependencies.length > 0 || n.dependents.length > 0).map(node => (
                <div key={node.id} className={`rounded-lg border p-3 transition-colors ${node.enabled ? "border-border/50 hover:bg-muted/10" : "border-destructive/15 bg-destructive/3"}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <GitBranch className="h-3.5 w-3.5 text-muted-foreground/60" />
                    <span className="font-medium text-[12px] text-foreground">{node.label}</span>
                    {!node.enabled && <Badge variant="outline" className="text-[9px] text-destructive border-destructive/20 font-normal">Disabled</Badge>}
                  </div>
                  <div className="flex flex-wrap gap-1 ml-5">
                    {node.dependencies.map(d => {
                      const dep = depGraph.find(n => n.id === d);
                      return (
                        <Badge key={d} variant="outline" className={`text-[9px] font-normal ${dep?.enabled ? "border-border" : "border-destructive/20 text-destructive"}`}>
                          ← {d} {!dep?.enabled && <AlertTriangle className="h-2.5 w-2.5 ml-0.5" />}
                        </Badge>
                      );
                    })}
                    {node.dependents.map(d => (
                      <Badge key={d} variant="outline" className="text-[9px] bg-primary/5 border-primary/15 font-normal">→ {d}</Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* ══ Governance Audit Log ══ */}
        <TabsContent value="audit" className="mt-5">
          <div className="rounded-xl border border-border bg-card/50 p-5">
            <h3 className="text-sm font-semibold text-foreground mb-1">Governance Audit Log</h3>
            <p className="text-[11px] text-muted-foreground mb-4">All administrative actions are recorded here</p>
            {auditLog.length === 0 ? (
              <p className="text-[11px] text-muted-foreground py-12 text-center">No changes recorded yet.</p>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {auditLog.map(entry => {
                  const typeLabels: Record<string, string> = {
                    capability_override: "Capability Override", user_status_change: "User Status",
                    user_role_change: "Role Change", user_capability_override: "User Access",
                    tenant_profile_change: "Tenant Profile", tenant_status_change: "Tenant Status",
                    tenant_capability_override: "Tenant Override",
                    environment_change: "Environment", profile_change: "Profile",
                  };
                  const typeColors: Record<string, string> = {
                    user_status_change: "bg-primary/8 text-primary border-primary/15",
                    user_role_change: "bg-warning/8 text-warning border-warning/15",
                    capability_override: "bg-success/8 text-success border-success/15",
                    tenant_profile_change: "bg-accent/20 text-accent-foreground border-accent/20",
                    environment_change: "bg-destructive/8 text-destructive border-destructive/15",
                  };
                  return (
                    <div key={entry.id} className="rounded-lg border border-border/50 p-3 hover:bg-muted/10 transition-colors">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={`text-[9px] font-normal ${typeColors[entry.actionType] ?? "bg-muted/30"}`}>
                            {typeLabels[entry.actionType] ?? entry.actionType}
                          </Badge>
                          <span className="text-[12px] font-medium text-foreground">{entry.targetId}</span>
                        </div>
                        <span className="text-[10px] text-muted-foreground/60">{new Date(entry.timestamp).toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px]">
                        <span className="font-mono text-muted-foreground bg-muted/20 px-1.5 py-0.5 rounded text-[10px]">
                          {JSON.stringify(entry.oldValue).slice(0, 40)}
                        </span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground/30" />
                        <span className="font-mono text-foreground bg-muted/20 px-1.5 py-0.5 rounded text-[10px]">
                          {JSON.stringify(entry.newValue).slice(0, 40)}
                        </span>
                      </div>
                      <p className="text-[10px] text-muted-foreground/60 mt-1.5">
                        {entry.actor} ({entry.actorRole}) · {entry.reason} · {entry.scope}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* ══════════════════════════════════════════ */}
      {/* ── EXPLAINABILITY DIALOG ── */}
      {/* ══════════════════════════════════════════ */}
      <Dialog open={!!showExplainer} onOpenChange={() => setShowExplainer(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center">
                <Info className="h-4 w-4 text-primary" />
              </div>
              Why is this the current state?
            </DialogTitle>
            <DialogDescription className="text-[11px]">
              {explainerData
                ? `Resolved to "${explainerData.finalState.replace("_", " ")}" via ${explainerData.primaryDecidingPass} pass`
                : "Loading..."}
            </DialogDescription>
          </DialogHeader>
          {explainerData && (
            <div className="space-y-4">
              {/* Plain language summary */}
              <div className="rounded-lg bg-primary/5 border border-primary/10 p-4">
                <p className="text-[12px] text-foreground leading-relaxed">{explainerData.explanation}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <InfoCell label="Final State" value={explainerData.finalState.replace("_", " ")} />
                <InfoCell label="Deciding Pass" value={explainerData.primaryDecidingPass} />
              </div>

              {explainerData.overriddenBy && (
                <div className="flex items-center gap-2 text-[11px] p-2.5 rounded-lg bg-warning/8 border border-warning/15">
                  <Zap className="h-3.5 w-3.5 text-warning shrink-0" />
                  <span className="text-warning">Overridden by: {explainerData.overriddenBy}</span>
                </div>
              )}
              {explainerData.roleRestriction && (
                <div className="flex items-center gap-2 text-[11px] p-2.5 rounded-lg bg-destructive/8 border border-destructive/15">
                  <ShieldAlert className="h-3.5 w-3.5 text-destructive shrink-0" />
                  <span className="text-destructive">{explainerData.roleRestriction}</span>
                </div>
              )}
              {explainerData.dependencyIssues.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Dependency Issues</p>
                  {explainerData.dependencyIssues.map((d, i) => (
                    <p key={i} className="text-[11px] text-destructive pl-3 border-l-2 border-destructive/20">{d}</p>
                  ))}
                </div>
              )}
              {explainerData.tenantInfluence && (
                <div className="flex items-center gap-2 text-[11px] p-2.5 rounded-lg bg-primary/8 border border-primary/15">
                  <Building2 className="h-3.5 w-3.5 text-primary shrink-0" />
                  <span className="text-primary">Tenant: {explainerData.tenantInfluence}</span>
                </div>
              )}

              {/* Pass timeline */}
              {showExplainer && (() => {
                const cap = resolved.find(r => r.id === showExplainer);
                if (!cap || cap.passTrace.length === 0) return null;
                return (
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Decision Timeline</p>
                    <div className="space-y-1">
                      {cap.passTrace.map((t, i) => (
                        <div key={i} className="flex items-center gap-2 text-[11px] p-2 rounded-lg bg-muted/15">
                          <div className="h-5 w-5 rounded-full bg-primary/8 flex items-center justify-center text-[9px] font-semibold text-primary shrink-0">{i + 1}</div>
                          <Badge variant="outline" className="text-[9px] shrink-0 font-normal">{t.pass}</Badge>
                          <span className="text-muted-foreground flex-1">{t.action}</span>
                          <ArrowRight className="h-3 w-3 text-muted-foreground/30 shrink-0" />
                          <span className={`font-mono text-[10px] ${t.after.enabled ? "text-success" : "text-destructive"}`}>
                            {t.after.enabled ? "ON" : "OFF"} · {t.after.mode}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ══════════════════════════════════════════ */}
      {/* ── SIMULATION / IMPACT / CONFIRM DIALOG ── */}
      {/* ══════════════════════════════════════════ */}
      <Dialog open={!!simResult} onOpenChange={() => { setSimResult(null); setPendingChange(null); setConfirmText(""); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center">
                <FlaskConical className="h-4 w-4 text-primary" />
              </div>
              Preview Changes
            </DialogTitle>
            <DialogDescription className="text-[11px]">
              {needsTypedConfirm
                ? "High-risk change — review impact carefully, then type CONFIRM to proceed."
                : "Review the impact below, then apply or cancel."}
            </DialogDescription>
          </DialogHeader>
          {simResult && (
            <div className="space-y-5">
              {/* Risk Level */}
              {(() => {
                const rc = RISK_CONFIG[simResult.impactPreview.riskLevel];
                const RiskIcon = rc.icon;
                return (
                  <div className={`flex items-center gap-3 p-4 rounded-xl border ${rc.color}`}>
                    <RiskIcon className="h-5 w-5 shrink-0" />
                    <div>
                      <p className="font-semibold text-[13px]">{rc.label}</p>
                      <p className="text-[11px] opacity-70">
                        {simResult.impactPreview.affectedCapabilities} capabilities · {simResult.impactPreview.affectedUsers} users · {simResult.impactPreview.affectedTenants} tenants
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Critical Warnings */}
              {simResult.impactPreview.criticalWarnings.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[10px] text-destructive uppercase tracking-wider font-semibold">Critical Warnings</p>
                  {simResult.impactPreview.criticalWarnings.map((w, i) => (
                    <div key={i} className="flex items-center gap-2 text-[11px] p-2.5 rounded-lg bg-destructive/8 border border-destructive/15">
                      <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
                      <span className="text-destructive">{w}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Affected routes & modules */}
              <div className="grid grid-cols-2 gap-4">
                {simResult.impactPreview.affectedRoutes.length > 0 && (
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Affected Routes</p>
                    <div className="flex flex-wrap gap-1">
                      {simResult.impactPreview.affectedRoutes.map(r => (
                        <Badge key={r} variant="outline" className="text-[9px] font-mono font-normal">{r}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {simResult.impactPreview.affectedModules.length > 0 && (
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Affected Categories</p>
                    <div className="flex flex-wrap gap-1">
                      {simResult.impactPreview.affectedModules.map(m => (
                        <Badge key={m} variant="outline" className="text-[9px] capitalize font-normal">{CATEGORY_LABELS[m] ?? m}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Diff table */}
              {simResult.diff.length > 0 ? (
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Changes ({simResult.diff.length})</p>
                  <div className="rounded-lg border border-border/50 overflow-hidden">
                    <div className="grid grid-cols-[1fr_70px_90px_90px] gap-0 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-4 py-2 bg-muted/20 border-b border-border/50">
                      <span>Capability</span><span>Field</span><span>Before</span><span>After</span>
                    </div>
                    <div className="divide-y divide-border/20 max-h-[250px] overflow-y-auto">
                      {simResult.diff.map((d, i) => (
                        <div key={i} className="grid grid-cols-[1fr_70px_90px_90px] gap-0 text-[11px] px-4 py-2 items-center hover:bg-muted/10">
                          <span className="font-medium text-foreground">{d.label}</span>
                          <span className="text-muted-foreground capitalize">{d.field}</span>
                          <span className="font-mono text-[10px] text-destructive/70">{d.before}</span>
                          <span className="font-mono text-[10px] text-success font-medium">{d.after}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-[11px] text-muted-foreground">No capability changes detected.</div>
              )}

              {/* Behavioral Changes */}
              {simResult.impactPreview.behavioralChanges.length > 0 && simResult.impactPreview.behavioralChanges.length <= 8 && (
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Behavioral Changes</p>
                  <div className="space-y-1">
                    {simResult.impactPreview.behavioralChanges.map((b, i) => (
                      <p key={i} className="text-[11px] text-muted-foreground pl-3 border-l-2 border-primary/20">{b}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Typed confirmation */}
              {needsTypedConfirm && (
                <div className="space-y-2.5 pt-3 border-t border-border/50">
                  <p className="text-[11px] text-destructive font-medium">Type "CONFIRM" to proceed with this {simResult.impactPreview.riskLevel}-risk change:</p>
                  <Input value={confirmText} onChange={e => setConfirmText(e.target.value)}
                    placeholder="Type CONFIRM" className="h-9 text-[11px] font-mono" />
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 pt-2">
            <Button variant="ghost" size="sm" className="text-[11px]" onClick={() => { setSimResult(null); setPendingChange(null); setConfirmText(""); }}>
              Cancel
            </Button>
            <Button size="sm" className="text-[11px] gap-1.5"
              disabled={!!needsTypedConfirm && confirmText !== "CONFIRM"}
              onClick={applyPendingChange}>
              <ShieldCheck className="h-3.5 w-3.5" /> Apply Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Sub-components ──

function StatusPill({ label, value, muted, warn }: { label: string; value: string | number; muted?: boolean; warn?: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] text-muted-foreground/60">{label}</span>
      <span className={`text-[11px] font-semibold ${warn ? "text-warning" : muted ? "text-foreground" : "text-muted-foreground"}`}>{value}</span>
    </div>
  );
}

function InfoCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-2.5 rounded-lg bg-muted/15">
      <p className="text-[10px] text-muted-foreground/60 mb-0.5">{label}</p>
      <p className="text-[12px] font-semibold text-foreground capitalize">{value}</p>
    </div>
  );
}

function CapabilityRow({ cap, selected, onClick, onExplain }: { cap: ResolvedCapability; selected: boolean; onClick: () => void; onExplain: () => void }) {
  const ModeIcon = MODE_ICONS[cap.effectiveMode];
  return (
    <div className={`flex items-center gap-3 rounded-lg border p-3 transition-all ${
      selected ? "border-primary/25 bg-primary/3" : "border-border/50 hover:bg-muted/10"
    }`}>
      <button onClick={onClick} className="flex items-center gap-3 flex-1 min-w-0 text-left">
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${MODE_COLORS[cap.effectiveMode]}`}>
          <ModeIcon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-semibold text-foreground">{cap.label}</span>
            <Badge className={`text-[9px] font-normal ${TIER_COLORS[cap.tier]}`} variant="outline">{cap.tier}</Badge>
            {!cap.accessible && <Badge variant="outline" className="text-[9px] text-destructive border-destructive/15 font-normal">No Access</Badge>}
          </div>
          <p className="text-[11px] text-muted-foreground/60 truncate mt-0.5">{cap.reason}</p>
        </div>
        <Badge variant="outline" className={`text-[9px] shrink-0 capitalize font-normal ${MODE_COLORS[cap.effectiveMode]}`}>
          {cap.effectiveMode.replace("_", " ")}
        </Badge>
      </button>
      <button onClick={(e) => { e.stopPropagation(); onExplain(); }}
        className="text-muted-foreground/40 hover:text-primary transition-colors shrink-0 p-1">
        <Info className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
